"use client";

import Mixpanel from "@/utils/analytics/Mixpanel";
import { Flashcard, FlashcardSet } from "@knowt/syncing/graphql/schema";
import { useFlashcardSet } from "@knowt/syncing/hooks/flashcards/useFlashcards";
import { useCurrentUser } from "@knowt/syncing/hooks/user/useCurrentUser";
import { createContext, useContextSelector } from "@knowt/syncing/utils/use-context-selector";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { report } from "@/utils/analytics/logging";
import { getSavingErrorMessage, throwIfInputIsNotValid } from "../utils";
import { SetEditingContextValue, SavingMessage, FlashcardForceUpdateContentCb } from "../types";
import { useAutoSave } from "@/hooks/useAutoSave";
import { useCommandSave } from "@/hooks/useCommandSave";
import { VirtuosoHandle } from "react-virtuoso";
import { useRouter } from "@bprogress/next";
import { FlexColumnAlignJustifyCenter } from "@/components/Flex";
import { themeColors } from "@/utils/themeColors";

const SetEditingContext = createContext<SetEditingContextValue>({
    flashcardSet: undefined,
    isSaving: false,
    pendingUpdates: { current: { metadata: {}, flashcards: {} } },
    deleteFlashcard: () => {
        throw new Error("`deleteFlashcard` is not implemented");
    },
    updateFlashcardSetMetaData: () => {
        throw new Error("`updateFlashcardSetMetaData` is not implemented");
    },
    updateFlashcards: () => {
        throw new Error("`updateFlashcards` is not implemented");
    },
    handleSwapTermsDefinitions: () => {
        throw new Error("`handleSave` is not implemented");
    },
    handleSave: async () => {
        throw new Error("`handleSave` is not implemented");
    },
    putPendingUpdatesInSWRCache: async () => {
        throw new Error("`putPendingUpdatesInSWRCache` is not implemented");
    },
    virtuosoRef: null,
    isChatSplitSectionOpen: false,
    setIsChatSplitSectionOpen: () => {
        throw new Error("`setIsChatSplitSectionOpen` is not implemented");
    },
    addFlashcard: () => {
        throw new Error("`addFlashcard` is not implemented");
    },
    addFlashcards: () => {
        throw new Error("`addFlashcards` is not implemented");
    },
    deleteFlashcards: () => {
        throw new Error("`deleteFlashcards` is not implemented");
    },
    flashcardsHandlesMap: new Map(),
    lastSaved: 0,
    readOnly: false,
});

/**
 * Should consists mostly anything related to flashcardSet saving handlers/states
 */
export const SetEditingContextProvider = ({
    children,
    flashcardSetId,
    autoSave = true,
    initialFlashcardSet,
}: {
    children: React.ReactNode;
    flashcardSetId: string;
    autoSave: boolean;
    initialFlashcardSet?: FlashcardSet | null;
}) => {
    const virtuosoRef = useRef<VirtuosoHandle>(null);
    const { sharingEnabled, user, loginInProgress, userId } = useCurrentUser();
    const router = useRouter();

    const [isSaving, setIsSaving] = useState(false);

    const { current: flashcardsHandlesMap } = useRef<Map<string, FlashcardForceUpdateContentCb>>(new Map());

    const {
        updateFlashcardSetMetaData,
        updateFlashcards,
        deleteFlashcard,
        deleteFlashcards,
        getCache,
        commitUpdates,
        flashcardSet,
        isDirty,
        addFlashcard,
        addFlashcards,
        lastSaved,
    } = useFlashcardSet({
        flashcardSetId,
        autoCommit: false,
        fallbackData: initialFlashcardSet ?? undefined,
    });

    const readOnly = userId !== flashcardSet?.userId;

    const [_isChatSplitSectionOpen, _setIsChatSplitSectionOpen] = useState(
        (!!flashcardSet?.mediaId || !!flashcardSet?.noteId) && flashcardSet?.draft
    );

    const setIsChatSplitSectionOpen = useCallback((value: boolean) => {
        _setIsChatSplitSectionOpen(value);
    }, []);

    const initialChatOpenDone = useRef(false);
    useEffect(() => {
        if (readOnly || initialChatOpenDone.current) return;

        if ((flashcardSet?.mediaId || flashcardSet?.noteId) && flashcardSet?.draft) {
            setIsChatSplitSectionOpen(true);
            initialChatOpenDone.current = true;
        }
    }, [flashcardSet?.draft, flashcardSet?.mediaId, flashcardSet?.noteId, setIsChatSplitSectionOpen, readOnly]);

    useEffect(() => {
        if (!flashcardSet || loginInProgress) return;

        if (flashcardSet === null) {
            router.replace("/");
        }

        if (flashcardSet.userId !== userId || flashcardSet.lock) {
            router.replace(`/flashcards/${flashcardSet.flashcardSetId}`);
        }
    }, [userId, loginInProgress, flashcardSet, router]);

    const isCreateMode = !!flashcardSet?.draft;

    const pendingUpdates = useRef<{
        metadata: Partial<Omit<FlashcardSet, "flashcards">>;
        flashcards: { [flashcardId: string]: Partial<Flashcard> };
    }>({ metadata: {}, flashcards: {} });

    const putPendingUpdatesInSWRCache = useCallback(async () => {
        // clear `pendingUpdates` before awaiting updating the cache,
        // to make sure we're not resetting any new updates that might
        // have been added while we were awaiting updating the cache.
        const metadataUpdates = pendingUpdates.current.metadata;
        const flashcardsUpdates = pendingUpdates.current.flashcards;
        pendingUpdates.current = { metadata: {}, flashcards: {} };

        if (Object.keys(metadataUpdates).length) {
            await updateFlashcardSetMetaData(metadataUpdates);
        }

        if (Object.keys(flashcardsUpdates).length) {
            await updateFlashcards(oldFlashcards =>
                oldFlashcards.map(flashcard => ({ ...flashcard, ...flashcardsUpdates[flashcard.flashcardId] }))
            );
        }
    }, [updateFlashcardSetMetaData, updateFlashcards]);

    const safeCommitUpdates = useCallback(async () => {
        await putPendingUpdatesInSWRCache();
        await commitUpdates();
    }, [commitUpdates, putPendingUpdatesInSWRCache]);

    const handleSave = useCallback(
        async (skipCreation = false) => {
            try {
                await putPendingUpdatesInSWRCache();
                const currentFlashcardSetCache = await getCache();
                await throwIfInputIsNotValid(currentFlashcardSetCache);
            } catch (error) {
                toast.error(error?.message || SavingMessage.FAILURE);
                return false;
            }

            const updateInfo = { ...pendingUpdates };

            try {
                setIsSaving(true);

                if (!skipCreation) {
                    if (isCreateMode) {
                        await updateFlashcardSetMetaData({
                            draft: false,
                            public: flashcardSet?.public ?? true,
                            ...(flashcardSet?.classId || !sharingEnabled ? { public: false } : {}),
                        });
                    }

                    if (isCreateMode) {
                        Mixpanel.track("Flashcard Set - Created", {
                            flashcardSetId,
                            noteId: flashcardSet?.noteId,
                            classId: flashcardSet?.classId,
                            public: flashcardSet?.public,
                            mediaId: flashcardSet?.mediaId,
                            deadline: flashcardSet?.deadline,
                            xp: user?.xp,
                            level: user?.level,
                            streak: user?.streak,
                            coins: user?.coins,
                            gamificationRecords: user?.records,
                        });
                    } else {
                        toast.success("Saved!");
                    }
                }

                await safeCommitUpdates();

                return true;
            } catch (error) {
                report(error, "manualSaveAndGoBack", updateInfo);
                toast.error(getSavingErrorMessage(error, isCreateMode));
                return false;
            } finally {
                setIsSaving(false);
            }
        },
        [
            putPendingUpdatesInSWRCache,
            getCache,
            isCreateMode,
            updateFlashcardSetMetaData,
            sharingEnabled,
            safeCommitUpdates,
            flashcardSetId,
            flashcardSet?.noteId,
            flashcardSet?.classId,
            flashcardSet?.public,
            flashcardSet?.mediaId,
            flashcardSet?.deadline,
            user,
        ]
    );

    const handleSwapTermsDefinitions = useCallback(async () => {
        // we put the data in the cache because `updateFlashcards` below is reading form it
        await putPendingUpdatesInSWRCache();

        await updateFlashcards(oldFlashcards =>
            oldFlashcards.map(flashcard => ({
                ...flashcard,
                term: flashcard.definition,
                definition: flashcard.term,
                image: flashcard.secondaryImage,
                secondaryImage: flashcard.image,
            }))
        );

        const currentFlashcardSetCache = await getCache();

        if (currentFlashcardSetCache?.flashcards?.length) {
            for (const flashcard of currentFlashcardSetCache.flashcards) {
                const handle = flashcardsHandlesMap.get(flashcard.flashcardId);
                if (handle) handle.forceUpdateContent(flashcard);
            }
        }
    }, [getCache, putPendingUpdatesInSWRCache, updateFlashcards]);

    useAutoSave({
        isDirty: useCallback(() => {
            if (!autoSave || isSaving) return false;

            return (
                isDirty ||
                Object.keys(pendingUpdates.current.flashcards).length > 0 ||
                Object.keys(pendingUpdates.current.metadata).length > 0
            );
        }, [isDirty, isSaving, autoSave]),
        save: safeCommitUpdates,
        interval: 10_000,
        block: true,
    });

    useCommandSave({
        successMsg: SavingMessage.SUCCESS,
        errorMsg: SavingMessage.FAILURE,
        backoffTime: 5000,
        save: safeCommitUpdates,
    });

    return (
        <SetEditingContext.Provider
            value={{
                flashcardSet,
                updateFlashcards,
                pendingUpdates,
                addFlashcard,
                addFlashcards,
                deleteFlashcard,
                deleteFlashcards,
                putPendingUpdatesInSWRCache,
                handleSwapTermsDefinitions,
                updateFlashcardSetMetaData,
                handleSave,
                isChatSplitSectionOpen: !!_isChatSplitSectionOpen,
                setIsChatSplitSectionOpen,
                flashcardsHandlesMap,
                isSaving,
                lastSaved,
                virtuosoRef,
                readOnly,
            }}>
            {flashcardSet ? (
                children
            ) : (
                <FlexColumnAlignJustifyCenter
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "90vh",
                        width: "100%",
                    }}>
                    <l-helix size={150} color={themeColors.neutralBlack} />
                </FlexColumnAlignJustifyCenter>
            )}
        </SetEditingContext.Provider>
    );
};

/** Selector of `SetEditingSaverProvider` which consists anything related to flashcardSet saving handlers/states */
export const useSetEditingContextSelector = <T,>(selector: (value: SetEditingContextValue) => T) =>
    useContextSelector(SetEditingContext, selector);
